import { IconAccessTime, IconCloseCircle } from 'assets/img';
import type { Column } from 'react-table';
import { Box, styled, Tooltip, Typography } from '@mui/material';
import { ContextMenuButton, ContextMenuItem } from 'redesign';
import { useGetCancelationDialog } from 'shared/components/shift-detail-modal/hooks/shared/cancelation-dialog/cancelation-dialog';
import { HQCancelShiftClaimAction } from 'shared/components/shift-detail-modal/hooks/hq/actions/claimant-actions/cancel-shift-claim-action';
import { TFunctionNonStrict } from 'i18next';
import { mapPlacementStateToCopy } from './parse-table-data';
import { PlacementsCellProp, PlacementsColumnDefinition } from '../placements.types';

const getPlacementColumnDefinition: (
  t: TFunctionNonStrict<'hq-screen-specific', undefined>
) => Column<PlacementsColumnDefinition>[] = (t) => {
  const workerName: Column<PlacementsColumnDefinition> = {
    Header: () => (
      <Header>{t('placements.table_headers.worker', { ns: 'hq-screen-specific' })}</Header>
    ),
    accessor: 'workerName',
    Cell: WorkerCell,
  };

  const companyBranchOfficeInfo: Column<PlacementsColumnDefinition> = {
    Header: () => (
      <Header>
        {t('placements.table_headers.company_and_branch_office', { ns: 'hq-screen-specific' })}
      </Header>
    ),
    accessor: 'companyBranchOfficeInfo',
    Cell: CompanyBranchOfficeInfoCell,
  };

  const departmentName: Column<PlacementsColumnDefinition> = {
    Header: () => (
      <Header>{t('placements.table_headers.department', { ns: 'hq-screen-specific' })}</Header>
    ),
    accessor: 'departmentName',
    width: '150px',
    Cell: DepartmentCell,
  };

  const jobName: Column<PlacementsColumnDefinition> = {
    Header: () => (
      <Header>{t('placements.table_headers.job_title', { ns: 'hq-screen-specific' })}</Header>
    ),
    accessor: 'jobName',
    width: '150px',
    Cell: JobCell,
  };

  const jobType: Column<PlacementsColumnDefinition> = {
    Header: () => (
      <Header>{t('placements.table_headers.job_type', { ns: 'hq-screen-specific' })}</Header>
    ),
    accessor: 'jobType',
    width: '150px',
    Cell: JobTypeCell,
  };

  const workerEmploymentType: Column<PlacementsColumnDefinition> = {
    Header: () => (
      <Header>
        {t('placements.table_headers.worker_employment_type', { ns: 'hq-screen-specific' })}
      </Header>
    ),
    accessor: 'workerEmploymentType',
    width: '150px',
    Cell: WorkerEmploymentTypeCell,
  };

  const scheduledTime: Column<PlacementsColumnDefinition> = {
    Header: () => (
      <Header>{t('placements.table_headers.date_time', { ns: 'hq-screen-specific' })}</Header>
    ),
    accessor: 'dateTime',
    Cell: DateTimeCell,
  };

  const state: Column<PlacementsColumnDefinition> = {
    Header: () => (
      <Header>{t('placements.table_headers.status', { ns: 'hq-screen-specific' })}</Header>
    ),
    accessor: 'state',
    Cell: StateCell,
    width: '150px',
  };
  const actions: Column<PlacementsColumnDefinition> = {
    Header: () => (
      <Header>{t('placements.table_headers.action', { ns: 'hq-screen-specific' })}</Header>
    ),
    accessor: 'actions',
    Cell: ActionCell,
    width: '100px',
  };

  return [
    workerName,
    companyBranchOfficeInfo,
    departmentName,
    jobName,
    jobType,
    workerEmploymentType,
    scheduledTime,
    state,
    actions,
  ];
};

const Header = ({ children }: React.PropsWithChildren<unknown>) => (
  <Typography variant="body2" style={{ fontWeight: 'bold' }}>
    {children}
  </Typography>
);

const WorkerCell = (cell: PlacementsCellProp<'workerName'>) => {
  return (
    <Typography variant="body1" noWrap>
      {cell.value}
    </Typography>
  );
};

const CompanyBranchOfficeInfoCell = (cell: PlacementsCellProp<'companyBranchOfficeInfo'>) => {
  return (
    <Tooltip
      style={{ display: 'flex', gap: '3px' }}
      title={
        <Box>
          {cell.value.companyName}
          <br />
          {cell.value.branchOfficeName}
        </Box>
      }
    >
      <BoxInfo>
        <TypographyWithPreLine variant="body1" noWrap>
          {`${cell.value.companyName} \n ${cell.value.branchOfficeName}`}
        </TypographyWithPreLine>
      </BoxInfo>
    </Tooltip>
  );
};

const JobCell = (cell: PlacementsCellProp<'jobName'>) => {
  return (
    <Typography variant="body1" noWrap>
      {cell.value}
    </Typography>
  );
};

const JobTypeCell = (cell: PlacementsCellProp<'jobType'>) => {
  return (
    <Typography variant="body1" noWrap>
      {cell.value}
    </Typography>
  );
};

const DepartmentCell = (cell: PlacementsCellProp<'departmentName'>) => {
  return (
    <Tooltip placement="bottom-start" title={cell.value}>
      <Typography variant="body1" noWrap>
        {cell.value}
      </Typography>
    </Tooltip>
  );
};
const WorkerEmploymentTypeCell = (cell: PlacementsCellProp<'workerEmploymentType'>) => {
  return (
    <Typography variant="body1" noWrap>
      {cell.value}
    </Typography>
  );
};

const StateCell = (cell: PlacementsCellProp<'state'>) => {
  return <Typography variant="body1">{cell.value}</Typography>;
};

const DateTimeCell = (cell: PlacementsCellProp<'dateTime'>) => {
  return (
    <BoxInfo>
      <Typography variant="body1" color="secondary">
        {cell.value.fromDateTime.toFormat('dd MMM yyyy')}
      </Typography>
      <BoxTime>
        <IconAccessTime />
        <Typography variant="body1">
          {cell.value.fromDateTime.toFormat('HH:mm') +
            '-' +
            cell.value.toDateTime.toFormat('HH:mm')}
        </Typography>
      </BoxTime>
    </BoxInfo>
  );
};

const ActionCell = ({ value }: PlacementsCellProp<'actions'>) => {
  const { handleCancelShiftClaim } = useGetCancelationDialog();
  const candidate = {
    claimId: value.placementId,
    isFlexWorker: value.worker.isFlexWorker,
    employmentType: value.worker.employmentType,
  };

  const shiftData = {
    mutableUntilDate: value.mutableUntilDate,
    isFreelance: value.isFreelance,
    id: value.placementId,
  };

  const actions: React.ReactNode[] = [];

  if (value.placementState == 'accepted') {
    actions.push(
      <ContextMenuItem
        onClick={() => handleCancelShiftClaim(candidate.claimId)}
        key={`cancel-claim`}
      >
        <IconCloseCircle />
        Annuleren met reden
      </ContextMenuItem>
    );
    actions.push(
      <HQCancelShiftClaimAction candidate={candidate} shiftData={shiftData} key="cancel" />
    );
  } else {
    actions.push(
      `Plaatsing kan niet worden geannuleerd, omdat het de volgende status heeft: ${mapPlacementStateToCopy(value.placementState)}.`
    );
  }

  return (
    <ContextMenuButton menuId={`placements-actions-menu-${value.worker.lastName}`}>
      {actions}
    </ContextMenuButton>
  );
};

const TypographyWithPreLine = styled(Typography)(({ theme }) => ({
  whiteSpace: 'pre-line',
  color: theme.palette.text.secondary,
}));

const BoxInfo = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
  whiteSpace: 'nowrap',
  alignItems: 'flexStart',
}));
const BoxTime = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'end',
  gap: theme.spacing(0.5),
}));

export { getPlacementColumnDefinition };
